import { reject } from 'ramda';
import { FC, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  ContextNotification,
  NotificationContext,
} from './NotificationContext';

type ContextNotificationWithoutKey = Pick<
  ContextNotification,
  Exclude<keyof ContextNotification, 'key' | 'open'>
>;

export const NotificationProvider: FC = ({ children }) => {
  const [notificationStack, setNotificationStack] = useState<
    ContextNotification[]
  >([]);

  const pushNotification = (
    notification: ContextNotificationWithoutKey,
  ): string => {
    const notificationStackLength = notificationStack.length;
    if (notificationStackLength >= 2) {
      // eslint-disable-next-line fp/no-loops
      for (let i = 0; i < notificationStackLength - 1; i++) {
        removeNotification(notificationStack[i].key);
      }
    }
    const key = uuidv4();
    setNotificationStack((prev) => [
      ...prev,
      { ...notification, key, open: true },
    ]);
    return key;
  };

  const exitedNotification = (key: string): void => {
    setNotificationStack((prev) =>
      reject(({ key: notificationKey }) => notificationKey === key, prev),
    );
  };

  const removeNotification = (key: string): void => {
    const copyState = notificationStack.map((notification) => {
      if (notification.key === key) {
        return { ...notification, open: false };
      }
      return notification;
    });
    setNotificationStack(copyState);
  };

  const setHeight = (
    snackItem: HTMLDivElement,
    key: string,
  ): ContextNotification | void => {
    let update = false;
    const copyState = notificationStack.map((notification) => {
      if (notification.key === key && !notification.height) {
        update = true;
        return { ...notification, height: snackItem.clientHeight };
      }
      return notification;
    });
    if (update) {
      setNotificationStack(copyState);
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        notificationStack,
        pushNotification,
        removeNotification,
        exitedNotification,
        setHeight,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
